import React, { useEffect } from "react";

import { Toolbar, Divider, AppBar, Typography, Box } from "@portex-pro/ui-components";
import FilesControl from "components/file-uploads/FilesControl";
import { GuideProvider } from "components/Guide";
import CargoGroupProvider from "features/cargo-groups/CargoGroupProvider";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ModeQuotes } from "types/Mode";

import { RecentTemplates } from "./_components/RecentTemplates";
import { SelectModeTabPanel } from "./_components/SelectModeTabPanel";
import { useSlices, useQuoteRequestSelector } from "./_store";
import { quoteRequestActions } from "./_store/quoteRequestSliceV2";
import { QuoteRequestAirForm } from "./air/form";
import { QuoteRequestDrayageForm } from "./drayage/form";
import { QuoteRequestFCLForm } from "./fcl/form";
import { QuoteRequestFTLForm } from "./ftl/form";
import { QuoteRequestLTLForm } from "./ltl/form";

const MODE_FORM_LOOKUP: Record<ModeQuotes, React.FC> = {
  FTL: QuoteRequestFTLForm,
  LTL: QuoteRequestLTLForm,
  DRAYAGE: QuoteRequestDrayageForm,
  FCL: QuoteRequestFCLForm,
  AIR: QuoteRequestAirForm,
};

export const QuoteRequestPage: React.FC = () => {
  useSlices();
  const { t } = useTranslation(["common", "quoteRequest"]);
  const { selectedMode, quoteRequest: airQuoteRequest } = useQuoteRequestSelector((state) => {
    return state.quoteRequestSliceV2;
  });
  const setSelectedModeAction = quoteRequestActions.useSetSelectedMode();
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const resetState = quoteRequestActions.useResetState();
  const dispatch = useDispatch();

  const FormComponent = MODE_FORM_LOOKUP[selectedMode];

  useEffect(() => {
    resetState();
    dispatch({ type: "quoteRequestSliceV2/resetState" });
    if (!!airQuoteRequest.id) {
      // The page shouldn't start with any quote requests
    }

    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <AppBar>
        <Toolbar variant="dense" disableGutters>
          <Box px={2.5}>
            <Typography color="textPrimary" variant="subtitle1">
              {t("quoteRequest:formTitle")}
            </Typography>
          </Box>
        </Toolbar>
        <Divider />
      </AppBar>
      <div className="m-4">
        <SelectModeTabPanel
          selected={selectedMode}
          onSelect={(mode) => {
            setQuoteRequestAction({ mode });
            setSelectedModeAction(mode);
          }}
        />
        <FilesControl
          fileIds={airQuoteRequest.fileIds ?? []}
          dropzoneOptions={{ maxFiles: 4, multiple: false, maxSize: 5e6 }}
          onMultiUploadSuccess={async (results) => {
            const fileIds = results.map(({ fileId }) => fileId);
            setQuoteRequestAction({ fileIds: [...(airQuoteRequest.fileIds ?? []), ...fileIds] });
          }}
          onUploadSuccess={async ({ fileId }) => {
            setQuoteRequestAction({ fileIds: [...(airQuoteRequest.fileIds ?? []), fileId] });
          }}
        >
          <CargoGroupProvider cargoGroups={airQuoteRequest.cargo_groups ?? []} quote_request_id={airQuoteRequest.id}>
            <GuideProvider>
              <FormComponent />
            </GuideProvider>
          </CargoGroupProvider>
        </FilesControl>
        <RecentTemplates />
      </div>
    </div>
  );
};
